'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var clsx = require('clsx');
var Dismiss = require('./UI/Dismiss.js');
var useChatUIState = require('../useChatUIState.js');
var ChatContent = require('./ChatContent.js');
var SlideableContent_module = require('./SlideableContent.module.css.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var clsx__default = /*#__PURE__*/_interopDefaultLegacy(clsx);

// component logic for sliding the chat convo into and out of view,
// as well as mobile viewport/keyboard handling and touch events.
const SlideableContent = ({
  footerEl,
  scrollContainerEl,
  children
}) => {
  const [isPartialOpen, setIsPartialOpen] = React.useState(false);
  const [isFullOpening, setIsFullOpening] = React.useState(false);
  const [isFullOpen, setIsFullOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const [mobileKeyboardVisible, setMobileKeyboardVisible] = React.useState(false);
  const [scrollStartY, setScrollStartY] = React.useState(null);
  const chatContainerEl = React.useRef(null);
  const topBarEl = React.useRef(null);
  const {
    state: chatUIState,
    update: updateChatState
  } = useChatUIState["default"]();
  const {
    openOnLoad,
    chatOpen,
    hadChatInteraction,
    currentScrollTop
  } = chatUIState;
  const handleViewportResize = () => {
    if (window?.visualViewport?.offsetTop === 0) setMobileKeyboardVisible(false);else setMobileKeyboardVisible(true);
  };
  const preventDefaultScroll = e => {
    e.preventDefault();
    e.stopImmediatePropagation();
  };
  const handleTouchMove = React.useCallback(e => {
    if (scrollStartY && e.touches[0].pageY < scrollStartY && scrollContainerEl.current?.scrollTop === currentScrollTop || scrollStartY && e.touches[0].pageY > scrollStartY && scrollContainerEl.current?.scrollTop === 0) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, [currentScrollTop, scrollContainerEl, scrollStartY]);
  const handleTouchStart = e => {
    setScrollStartY(e.touches[0].pageY);
  };
  React.useEffect(() => {
    if (!mobileKeyboardVisible) return;
    const chatContainerRef = chatContainerEl.current;
    chatContainerRef && chatContainerRef.addEventListener('touchmove', handleTouchMove);
    return () => {
      chatContainerRef && chatContainerRef.removeEventListener('touchmove', handleTouchMove);
    };
  }, [mobileKeyboardVisible, scrollStartY, handleTouchMove]);
  React.useEffect(() => {
    if (!mobileKeyboardVisible) return;
    const topBarRef = topBarEl.current;
    topBarRef && topBarRef.addEventListener('touchmove', preventDefaultScroll);
    const footerRef = footerEl.current;
    footerRef && footerRef.addEventListener('touchmove', preventDefaultScroll);
    chatContainerEl.current?.addEventListener('touchstart', handleTouchStart);
    return () => {
      window?.visualViewport?.removeEventListener('resize', handleViewportResize);
      topBarRef && topBarRef.removeEventListener('touchmove', preventDefaultScroll);
      footerRef && footerRef.addEventListener('touchmove', preventDefaultScroll);
    };
  }, [mobileKeyboardVisible, footerEl]);
  React.useEffect(() => {
    if (typeof window !== 'object' || !window.visualViewport) return;
    window.visualViewport.addEventListener('resize', handleViewportResize);

    // when the chat is partly open on initial load or when fully
    // open, expand its height to take up the whole viewport
    if (chatContainerEl.current && (mobileKeyboardVisible && isPartialOpen || isFullOpen)) {
      chatContainerEl.current.style['height'] = `${window.visualViewport?.height - ChatContent.CHAT_FOOTER_HEIGHT}px`;
    }

    // on initial view before any user interaction, keep the chat
    // half height of the viewport if the keyboard is closed.
    if (chatContainerEl.current && isPartialOpen && !mobileKeyboardVisible) {
      chatContainerEl.current.style['height'] = 'auto';
    }
    return () => {
      window?.visualViewport?.removeEventListener('resize', handleViewportResize);
    };
  }, [isPartialOpen, isFullOpen, mobileKeyboardVisible]);
  React.useEffect(() => {
    // behavior of chat only on initial load
    if (openOnLoad && !hadChatInteraction) {
      setIsPartialOpen(true);
    }

    // behavior when chat opens after initial load or on user interaction
    if (hadChatInteraction && chatOpen) {
      setIsFullOpening(true);
    }
  }, [openOnLoad, chatOpen, hadChatInteraction]);

  // don't let the background page scroll when chat is open
  React.useEffect(() => {
    if (typeof window !== 'object') return;
    if (chatOpen) {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${window.scrollY}px`;
    }
    if (!chatOpen) {
      document.body.style.position = '';
      document.body.style.top = '';
    }
  }, [chatOpen]);
  React.useEffect(() => {
    let fullOpeningTimer;
    if (isFullOpening) {
      const _timeout = 500;
      fullOpeningTimer = setTimeout(() => {
        setIsFullOpening(false);
        setIsPartialOpen(false);
        setIsFullOpen(true);
      }, _timeout);
    }
    return () => {
      if (fullOpeningTimer) {
        clearTimeout(fullOpeningTimer);
      }
    };
  }, [isFullOpening]);
  React.useEffect(() => {
    let closingTimer;
    if (isClosing) {
      const _timeout = 500;
      closingTimer = setTimeout(() => {
        setIsClosing(false);
        setIsPartialOpen(false);
        setIsFullOpen(false);
        updateChatState({
          ...chatUIState,
          chatOpen: false
        });
      }, _timeout);
    }
    return () => {
      if (closingTimer) {
        clearTimeout(closingTimer);
      }
    };
  }, [isClosing, chatUIState, updateChatState]);
  const dismissOnClick = () => {
    setIsClosing(true);
  };
  let animationName = '';
  if (isFullOpening) animationName = SlideableContent_module["default"].animateUp;else if (isClosing) animationName = SlideableContent_module["default"].animateDown;
  const classes = clsx__default["default"]({
    [SlideableContent_module["default"].container]: true,
    [animationName]: !!animationName,
    [SlideableContent_module["default"].chatOpen]: chatOpen
  });
  return /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null, /*#__PURE__*/React__default["default"].createElement("div", {
    className: classes,
    ref: chatContainerEl
  }, /*#__PURE__*/React__default["default"].createElement("div", {
    className: SlideableContent_module["default"].topChatBar,
    ref: topBarEl
  }, /*#__PURE__*/React__default["default"].createElement("div", {
    className: SlideableContent_module["default"].dismissContainer
  }, /*#__PURE__*/React__default["default"].createElement(Dismiss["default"], {
    onClick: () => {
      dismissOnClick();
    }
  }))), children));
};

exports["default"] = SlideableContent;
